/**
 * GlobalKurallarPage.jsx — Global Kurallar (ŞABLON)
 * Route: /Management/Projects/GlobalRules
 * Roller: [1,2]   ← Fuar Kuralları'ndan DAR: bu ekran ileride açılacak her fuarı etkiler
 *
 * ⚠️ NEDEN AYRI BİR EKRAN. Fuara özel kural yazmak günlük iştir; global şablona
 *    dokunmak ise bundan sonra açılacak HER fuarı etkileyen nadir bir iştir. İkisi
 *    aynı ekranda bir sekme/anahtar olduğunda yanlış kapsamda kaydetmek tek tıklık
 *    mesafedeydi. Bu yüzden global ayarlar buraya taşındı, menüde de bilinçli olarak
 *    daha az göze çarpan bir yere ("Ayarlar" altına) kondu; Fuar Kuralları ekranından
 *    buraya yapılabilen tek şey ŞABLONU KOPYALAMAK — okumak, yazmak değil.
 *
 * BURADA FARKLI "GLOBAL"LER VAR VE AYNI ŞEY DEĞİLLER:
 *
 * 1. ŞABLON (kural seti · serbest kW modu/kademeleri · kullanım kuralı değerleri)
 *    Backend'de `fuar_kural` için gerçek bir global seviye YOK — fuar başına tek satır.
 *    FE sözleşmesi olarak 0 numaralı sahte fuarı şablon yuvası ayırdık (bkz.
 *    fuarKuralService.js → GLOBAL_KURAL_FUAR_ID). Buradaki değerler ancak bir fuara
 *    KOPYALANDIĞINDA yürürlüğe girer:
 *      · yeni bir fuar oluşturulduğunda otomatik (bkz. slices/fuarSlice.js)
 *      · Fuar Kuralları ekranındaki "Global şablonu uygula" ile elle
 *    KOPYADIR, MİRAS DEĞİL: kopyalandıktan sonra iki taraf bağımsızdır. Bilinçli —
 *    bir fuar başladıktan sonra kuralları altından değişmemeli.
 *
 * 2. GLOBAL STAND TİPİ KURALLARI (fuar 0'ın `fuar_stand_tipi_kural` satırları)
 *    2026-09-17'den (#55/#57) beri ŞABLON DEĞİL, CANLI VARSAYILAN: her fuar bir alanı
 *    kendi satırlarında (tip → üst tipler) bulamazsa buradan devralır. Buradaki bir
 *    değişiklik, o alanı geçersiz kılmamış HER fuarı anında etkiler — açık fuarlar dahil.
 *    Ağaç + düzenleme FuarKurallariPage'deki StandTipiKuralTree ile ortak (fuarId = 0).
 *
 * 3. KULLANIM KURALI TANIMLARI (#53, katalog — fuardan bağımsız)
 *    Hangi kullanım kurallarının (Cam, Boya, …) VAR olduğu: ad, beyan sorusu, yasak mesajı,
 *    sıra, aktiflik. Silme yok, pasifleştirilir. Kod oluşturulduktan sonra değişmez.
 *
 * 4. GLOBAL BELGE KURALLARI (`belge_kural`, fuarId = null)
 *    Bunlar da GERÇEKTEN globaldir: kaskadda kendi iki seviyesi var (GLOBAL_KURAL /
 *    GLOBAL_PSS) ve kopyalanmadan, her fuarda alttan devrededir — ve her iki FUAR
 *    seviyesi bunların üstündedir.
 *
 * Bu ayrımı ekranda da yazıyoruz; yoksa "globali değiştirdim, fuar niye değişmedi?"
 * (ya da tersi: "niye değişti?") sorusu kaçınılmaz.
 *
 * 2026-09-17: kimlik doğrulama anahtarı kaldırıldı (#54) — artık her fuarda zorunlu.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import {
  Container, Card, CardBody, CardHeader, Row, Col,
  Button, Badge, Table, Nav, NavItem, NavLink,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Alert, Spinner, Input, FormGroup, Label, InputGroup, InputGroupText,
} from "reactstrap";
import {
  Globe, Save, Trash2, Plus, RefreshCw, AlertTriangle, Info,
  Zap, Pencil, Layers3, FileText, ShieldCheck, Ruler, Building2, ListChecks, Shirt,
} from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import BelgeKurallariPage from "./BelgeKurallariPage";
// #430: the company document rules by the company's nationality at the GLOBAL level (every fair)
import FirmaBelgeKurallariKarti from "../../../Components/Fuar/FirmaBelgeKurallariKarti";
// #374: the PPE (KKD) level template (fuar 0, copied into a fair from Fuar Kuralları › KKD) and the PPE item catalogue
import KkdKurallariKarti from "../../../Components/Fuar/KkdKurallariKarti";
import KkdKalemKatalogu from "../../../Components/Fuar/KkdKalemKatalogu";
// Stand tipi ağacı ve kullanım kuralı listesi Fuar Kuralları ile ORTAK (fuarId = 0 ile).
import { StandTipiKuralTree, KullanimKurallariList } from "./FuarKurallariPage";
import DuvarKurallariAlanlari from "../../../Components/Projects/DuvarKurallariAlanlari";
import {
  getGlobalKural, saveGlobalKuralWithMode,
  getGlobalSerbestKwBands,
  getGlobalStandTipiKuralEffective,
  listKullanimKurallari, createKullanimKurali, updateKullanimKurali,
  listFuarAlanlari, getFuarAlaniTavanlari, setFuarAlaniHMax, setSalonCeilingHeight,
  validateSerbestKwBands, validateSerbestKwBlok, deriveSerbestKwMode, resolveFreeKw,
  SERBEST_KW_MODE, SERBEST_KW_MODE_LABELS, GLOBAL_KURAL_FUAR_ID,
} from "../../../services/fuarKuralService";
import i18n from "../../../i18n";

// Global şablon her zaman CANLI backend'e gider: fuar 0 mock listesinde yok ve
// "şablon" kavramının mock'a düşmesi kullanıcıyı yanıltırdı.
const LIVE = true;

const TABS = [
  { key: "sablon", get label() { return i18n.t("projeler:globalKurallar.kuralSablonu"); }, icon: Zap },
  // 2026-09-17 (#55/#57): "Stand Tipi Şablonu" → canlı global varsayılanlar; kopyalanmıyor.
  { key: "standTipi", get label() { return i18n.t("projeler:globalKurallar.standTipiKurallari"); }, icon: Layers3 },
  { key: "kullanim", get label() { return i18n.t("projeler:globalKurallar.kullanimKuraliTanimlari"); }, icon: ListChecks },
  // 2026-09-15 (backlog #25): "Fuar Alanı Tavanları" kaldırıldı — fuar alanı geneli limit backend'de
  // emekli (410), salonun fiziksel tavanı ve izin verilen yüksekliği artık Fuar Alanları → Salonlar
  // listesinde. Aşağıdaki "tavan" kodu ulaşılamaz durumda; sayfa bir sonraki temizlikte sadeleşir.
  // { key: "tavan", label: "Fuar Alanı Tavanları", icon: Ruler },
  { key: "belge", get label() { return i18n.t("projeler:globalKurallar.globalBelgeKurallari"); }, icon: FileText },
  // #430: the company document rules by nationality, every fair (the foreign company's old code lists are seeded here)
  { key: "firmaBelge", get label() { return i18n.t("projeler:globalKurallar.firmaBelgeKurallari"); }, icon: Building2 },
  // #374: PPE (KKD) — the level template a fair copies, and the item catalogue (add / rename / retire)
  { key: "kkd", get label() { return i18n.t("projeler:globalKurallar.kkd"); }, icon: Shirt },
];
const TAB_KEYS = TABS.map((t) => t.key);

// Kopyalanan ŞABLON sekmesi — sayfa başındaki "şablona yazar" uyarısı yalnızca burada doğru.
// 2026-09-17: stand tipi sekmesi bu listeden çıktı (canlı miras, kendi uyarısı var).
const TEMPLATE_TABS = ["sablon"];
// Fuar 0 yuvasına yazan sekmeler — "Global Şablon · fuar #0" kartı bunlarda.
const GLOBAL_SLOT_TABS = ["sablon", "standTipi"];

const fmtM = (v) => (v == null ? "—" : `${Number(v).toFixed(2).replace(".", ",")} m`);

// Boş input ('') ile 0'ı ayırmak zorundayız — '' "temizle" (null), 0 bir sayı.
const toNullableNumber = (v) => (v === "" || v == null ? null : Number(v));

// WAS: const EMPTY_KATALOG_FORM = { ad: "", beyanSorusu: "", yasakMesaji: "", sira: "" };
// #337: + the English texts (kullanim_kurali_text). ad / beyanSorusu / yasakMesaji are the TURKISH.
const EMPTY_KATALOG_FORM = { ad: "", beyanSorusu: "", yasakMesaji: "", sira: "", adEn: "", beyanSorusuEn: "", yasakMesajiEn: "" };

// 2026-09-17: yerel StandTypeCascade kopyası ve stand tipi ekle/düzenle/sil modalları
// kaldırıldı — FuarKurallariPage.StandTipiKuralTree (fuarId = 0) hepsini içeriyor.

export default function GlobalKurallarPage() {
  const { t: tx } = useTranslation("projeler"); // #337 (tx: `t` is a local name below)
  // ?tab= ile açılabilsin — Fuar Kuralları'ndaki "Tanımları yönet" linki ?tab=kullanim gönderir.
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get("tab");
    return TAB_KEYS.includes(t) ? t : "sablon";
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  // #374: bumped when the PPE (KKD) catalogue changes, so the template card above it lists the new / retired item.
  const [kkdKatalogToken, setKkdKatalogToken] = useState(0);

  const [kural, setKural] = useState(null);
  const [kuralForm, setKuralForm] = useState({ serbestKw: "", serbestKwBlokKw: "", serbestKwBlokM2: "", komsuDuvarFarkM: "", minAciklikYuzde: "" });
  const [kwMode, setKwMode] = useState(SERBEST_KW_MODE.NONE);
  const [bands, setBands] = useState([]);
  const [usageRules, setUsageRules] = useState([]); // fuar 0'ın kullanimKurallari — PUT'ta otoriter

  // Canlı global stand tipi kuralları (fuar 0) — etkin ağaç.
  const [typeTree, setTypeTree] = useState([]);

  // ─── Kullanım kuralı tanımları (katalog) ────────────────────────────────────
  const [katalog, setKatalog] = useState([]);
  const [katalogLoading, setKatalogLoading] = useState(false);
  const [katalogReloadToken, setKatalogReloadToken] = useState(0);
  const [katalogModal, setKatalogModal] = useState(false);
  const [katalogEditing, setKatalogEditing] = useState(null); // null = yeni
  const [katalogForm, setKatalogForm] = useState(EMPTY_KATALOG_FORM);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  // ─── Fuar Alanı Tavanları sekmesi ───────────────────────────────────────────
  // Şablonun aksine bu sekme HİÇBİR ŞEY KOPYALAMAZ: yazdığı iki şey de mekâna ait
  // gerçekler ve o mekânda açılan her fuarda anında geçerli.
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState("");
  const [tavan, setTavan] = useState(null);
  const [tavanLoading, setTavanLoading] = useState(false);
  const [venueLimitInput, setVenueLimitInput] = useState("");
  const [ceilingInputs, setCeilingInputs] = useState({});
  const [venueConfirm, setVenueConfirm] = useState(false);
  // 409'un gövdesi çakışanları ADLANDIRIR — hatayı eyleme çevirmenin tek yolu onları basmak.
  const [ceilingConflict, setCeilingConflict] = useState(null);
  const [tavanReloadToken, setTavanReloadToken] = useState(0);

  useEffect(() => { document.title = i18n.t("projeler:globalKurallar.globalKurallarFds"); }, []);

  useEffect(() => {
    if (!successMsg) return undefined;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const [k, bandRows] = await Promise.all([
          getGlobalKural({ forceLive: LIVE }),
          getGlobalSerbestKwBands({ forceLive: LIVE }),
        ]);
        if (ignore) return;
        setKural(k);
        setKuralForm({
          serbestKw: k?.serbestKw ?? "",
          serbestKwBlokKw: k?.serbestKwBlokKw ?? "",
          serbestKwBlokM2: k?.serbestKwBlokM2 ?? "",
          komsuDuvarFarkM: k?.komsuDuvarFarkM ?? "",
          minAciklikYuzde: k?.minAciklikYuzde ?? "",
        });
        setUsageRules((k?.kullanimKurallari || []).map((r) => ({ ...r })));
        setBands(bandRows || []);
        setKwMode(deriveSerbestKwMode({ ...k, serbestKwKademeleri: bandRows }));

        // Etkin ağaç ayrı try — yeni uç patlarsa şablon sekmesi yine çalışmalı.
        try {
          const tree = await getGlobalStandTipiKuralEffective({ forceLive: LIVE });
          if (!ignore) setTypeTree(tree || []);
        } catch (e) { if (!ignore) { setTypeTree([]); setError(e.message); } }
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [reloadToken]);

  const refresh = useCallback(() => setReloadToken((t) => t + 1), []);
  const refreshTavan = useCallback(() => setTavanReloadToken((t) => t + 1), []);
  const refreshKatalog = useCallback(() => setKatalogReloadToken((t) => t + 1), []);

  // Katalog — pasifler dahil; sekmeye girince ve her katalog yazmasından sonra.
  useEffect(() => {
    if (activeTab !== "kullanim") return undefined;
    let ignore = false;
    async function run() {
      setKatalogLoading(true);
      try {
        const rows = await listKullanimKurallari({ includeInactive: true, forceLive: LIVE });
        if (!ignore) setKatalog(rows || []);
      } catch (e) {
        if (!ignore) { setKatalog([]); setError(e.message); }
      } finally {
        if (!ignore) setKatalogLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [activeTab, katalogReloadToken]);

  // Fuar alanı listesi bir kez — sekmeye ilk girişte. Uç gerçek oturum ister (401).
  useEffect(() => {
    if (activeTab !== "tavan" || venues.length > 0) return undefined;
    let ignore = false;
    (async () => {
      try {
        const rows = await listFuarAlanlari({ forceLive: LIVE });
        if (ignore) return;
        setVenues(rows || []);
        // Tek mekân varsa seçtirmenin anlamı yok.
        if ((rows || []).length === 1) setVenueId(String(rows[0].fuarAlaniId));
      } catch (e) {
        if (!ignore) setError(e.message);
      }
    })();
    return () => { ignore = true; };
  }, [activeTab, venues.length]);

  // Seçilen mekânın tavan görünümü. Bu bir BİRLEŞTİRME: mekânın her fuarı için
  // /h-max okunuyor (bkz. fuarKuralService → getFuarAlaniTavanlari), çünkü fuar
  // alanına göre okuyan bir uç yok.
  useEffect(() => {
    let ignore = false;
    (async () => {
      // Temizleme de efektin GÖVDESİNDE değil içeride: senkron setState cascading
      // render sayılıyor (react-hooks/set-state-in-effect).
      if (!venueId) { setTavan(null); return; }
      setTavanLoading(true);
      setError(null);
      try {
        const t = await getFuarAlaniTavanlari(Number(venueId), { forceLive: LIVE });
        if (ignore) return;
        setTavan(t);
        setVenueLimitInput(t?.fuarAlaniHMax == null ? "" : String(t.fuarAlaniHMax));
        const ceilings = {};
        (t?.halls || []).forEach((h) => {
          ceilings[h.salonId] = h.ceilingHeight == null ? "" : String(h.ceilingHeight);
        });
        setCeilingInputs(ceilings);
      } catch (e) {
        if (!ignore) { setTavan(null); setError(e.message); }
      } finally {
        if (!ignore) setTavanLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [venueId, tavanReloadToken]);

  async function runAction(fn, okMsg) {
    setActionLoading(true);
    setError(null);
    setCeilingConflict(null);
    try {
      await fn();
      refresh();
      setSuccessMsg(okMsg);
      return true;
    } catch (e) {
      setError(e.message);
      if (e.conflicts) setCeilingConflict(e.conflicts);
      return false;
    } finally {
      setActionLoading(false);
    }
  }

  // Tavan sekmesinin aksiyonları şablonu değil mekânı tazelemeli.
  async function runTavanAction(fn, okMsg) {
    setActionLoading(true);
    setError(null);
    setCeilingConflict(null);
    try {
      await fn();
      refreshTavan();
      setSuccessMsg(okMsg);
      return true;
    } catch (e) {
      setError(e.message);
      if (e.conflicts) setCeilingConflict(e.conflicts);
      return false;
    } finally {
      setActionLoading(false);
    }
  }

  // ⚠️ Servis `hMax` anahtarını gövdeye HER ZAMAN koyar: atlamak 400, null temizler.
  const handleSaveVenueLimit = () =>
    runTavanAction(
      () => setFuarAlaniHMax(Number(venueId), toNullableNumber(venueLimitInput), { forceLive: LIVE }),
      toNullableNumber(venueLimitInput) == null
        ? tx("globalKurallar.fuarAlaniSiniriTemizlendi")
        : tx("globalKurallar.fuarAlaniSiniriKaydedildiBu")
    );

  const handleSaveCeiling = (salonId) =>
    runTavanAction(
      () => setSalonCeilingHeight(Number(venueId), salonId, toNullableNumber(ceilingInputs[salonId]), { forceLive: LIVE }),
      toNullableNumber(ceilingInputs[salonId]) == null
        ? tx("globalKurallar.salonunFizikselTavaniTemizlendi")
        : tx("globalKurallar.salonunFizikselTavaniKaydedildi")
    );

  const updateKural = (field, value) => setKuralForm((f) => ({ ...f, [field]: value }));
  const updateUsageRule = (kod, patch) =>
    setUsageRules((rows) => rows.map((r) => (r.kod === kod ? { ...r, ...patch } : r)));
  const bandErrors = kwMode === SERBEST_KW_MODE.BANDS ? validateSerbestKwBands(bands) : [];
  const blokErrors = kwMode === SERBEST_KW_MODE.BLOCK
    ? validateSerbestKwBlok(kuralForm.serbestKwBlokKw, kuralForm.serbestKwBlokM2)
    : [];
  const blokTouched = kuralForm.serbestKwBlokKw !== "" || kuralForm.serbestKwBlokM2 !== "";

  // Aynı sıra kuralı fuar ekranındaki gibi (servisteki saveFuarKuralWithMode):
  // kademeye geçerken önce kural (sabit + blok null) sonra kademeler; diğer modlarda tersi.
  const handleSaveKural = () => {
    if (bandErrors.length || blokErrors.length) return Promise.resolve(false);
    return runAction(
      () => saveGlobalKuralWithMode(
        kwMode,
        {
          ...kuralForm,
          kullanimKurallari: usageRules.map((r) => ({ kod: r.kod, izinli: r.izinli, sorulsun: r.sorulsun })),
        },
        bands,
        { forceLive: LIVE }
      ),
      tx("globalKurallar.globalKuralSablonuKaydedildiBundan")
    );
  };

  const addBand = () => setBands((b) => [...b, { minM2: "", maxM2: "", serbestKw: "" }]);
  const updateBand = (i, field, value) =>
    setBands((b) => b.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));
  const removeBand = (i) => setBands((b) => b.filter((_, idx) => idx !== i));

  // Stand tipi ekle/düzenle/sil aksiyonları StandTipiKuralTree'de (fuarId = 0).

  // ─── Katalog aksiyonları ────────────────────────────────────────────────────
  // Katalog yazması hem listeyi hem şablonun kullanım kuralı satırlarını tazeler
  // (yeni/pasif kural şablon sekmesinde de görünmeli/kaybolmalı).
  const runKatalogAction = async (fn, okMsg) => {
    const ok = await runAction(fn, okMsg);
    if (ok) refreshKatalog();
    return ok;
  };

  function openNewKatalog() {
    setKatalogEditing(null);
    setKatalogForm({ ...EMPTY_KATALOG_FORM, sira: String(katalog.length + 1) });
    setKatalogModal(true);
  }
  function openEditKatalog(item) {
    setKatalogEditing(item);
    // WAS: ad: item.ad ?? "", beyanSorusu: item.beyanSorusu ?? "", yasakMesaji: item.yasakMesaji ?? "",
    // #337: the list answers in the UI language, so on an English screen item.ad is the ENGLISH name.
    // The Turkish fields load the stored Turkish (adTr...), or saving would write English into Turkish.
    setKatalogForm({
      ad: item.adTr ?? item.ad ?? "",
      beyanSorusu: item.beyanSorusuTr ?? item.beyanSorusu ?? "",
      yasakMesaji: item.yasakMesajiTr ?? item.yasakMesaji ?? "",
      sira: item.sira == null ? "" : String(item.sira),
      adEn: item.adEn ?? "",
      beyanSorusuEn: item.beyanSorusuEn ?? "",
      yasakMesajiEn: item.yasakMesajiEn ?? "",
    });
    setKatalogModal(true);
  }
  // #337: an English question or message has nowhere to live without the English name (the backend refuses it).
  const katalogEnEksik = !katalogForm.adEn.trim() && Boolean(katalogForm.beyanSorusuEn.trim() || katalogForm.yasakMesajiEn.trim());
  // WAS: const katalogInvalid = !katalogForm.ad.trim() || !katalogForm.beyanSorusu.trim();
  const katalogInvalid = !katalogForm.ad.trim() || !katalogForm.beyanSorusu.trim() || katalogEnEksik;

  async function handleSaveKatalog() {
    if (katalogInvalid) return;
    const ok = await runKatalogAction(
      () => (katalogEditing
        ? updateKullanimKurali(katalogEditing.kullanimKuraliId, katalogForm, { forceLive: LIVE })
        : createKullanimKurali(katalogForm, { forceLive: LIVE })),
      katalogEditing ? tx("globalKurallar.kullanimKuraliGuncellendi") : tx("globalKurallar.kullanimKuraliEklendiHerFuarda")
    );
    if (ok) setKatalogModal(false);
  }

  // Aktifleştirme onaysız; pasifleştirme her fuarın formunu etkilediği için onaylı.
  const handleSetAktif = (item, aktif) => runKatalogAction(
    () => updateKullanimKurali(item.kullanimKuraliId, { aktif }, { forceLive: LIVE }),
    // WAS: aktif ? `"${item.ad}" yeniden aktif.` : `"${item.ad}" pasifleştirildi.`
    aktif ? tx("globalKurallar.yenidenAktif", { ad: item.ad }) : tx("globalKurallar.pasiflestirildi", { ad: item.ad })
  );

  // Yalnızca SEÇİLİ modun alanları — "Yok"a geçince eski değer önizlemede kalmasın.
  const previewKural = {
    [SERBEST_KW_MODE.FLAT]: { serbestKw: toNullableNumber(kuralForm.serbestKw) },
    [SERBEST_KW_MODE.BLOCK]: {
      serbestKwBlokKw: toNullableNumber(kuralForm.serbestKwBlokKw),
      serbestKwBlokM2: toNullableNumber(kuralForm.serbestKwBlokM2),
    },
    [SERBEST_KW_MODE.BANDS]: { serbestKwKademeleri: bands },
  }[kwMode] ?? {};
  const freeKwPreview = resolveFreeKw(previewKural, 30);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("globalKurallar.globalKurallar")} pageTitle={tx("globalKurallar.ayarlar")} />

        {error && (
          <Alert color="danger">
            {error}
            {/* Tavan/fuar alanı 409'ları çakışanı ADLANDIRIR. Üç ayrı 409 aynı listeye
                düşüyor ve gövdeleri farklı — hangi alanlar geldiyse onu basıyoruz. */}
            {ceilingConflict?.length > 0 && (
              <ul className="mb-0 mt-2 small">
                {ceilingConflict.map((c, i) => (
                  <li key={c.salonToFuarId ?? c.fuarId ?? i}>
                    {[
                      c.salonName ?? (c.salonId != null ? `Salon #${c.salonId}` : null),
                      c.fuarName ?? (c.fuarId != null ? `Fuar #${c.fuarId}` : null),
                    ].filter(Boolean).join(" · ")}
                    {tx("globalKurallar.sozVerilmis", { fmtM: fmtM(c.salonHMax ?? c.fuarHMax) })}
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}
        {successMsg && <Alert color="success">{successMsg}</Alert>}

        {/* Bu uyarı yalnızca KOPYALANAN şablon sekmelerinde doğru. Tavan sekmesi
            kopyalamaz — mekânın gerçeklerini yazar ve anında geçerlidir; belge
            kuralları da miras alınır. İkisinde bu uyarıyı göstermek yanlış olurdu. */}
        {TEMPLATE_TABS.includes(activeTab) && (
        <Alert color="warning" className="d-flex align-items-start gap-2">
          <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
          <div>
            <div className="fw-semibold mb-1">{tx("globalKurallar.buSekmeTekBirFuara")}</div>
            <div className="small">
              {tx("globalKurallar.kuralSablonuSerbestElektrikVe")}{" "}<span className="fw-semibold">{tx("globalKurallar.bundanSonraOlusturulanHerFuara")}</span>{tx("globalKurallar.halihazirdaAcikFuarlarEtkilenmezOnlari")}
              <Link to="/Management/Fairs/Rules?tab=kural" className="fw-semibold">{tx("globalKurallar.fuarKurallari")}</Link>{" "}
              {tx("globalKurallar.ekranindaki")}{" "}<span className="fw-semibold">{tx("globalKurallar.globalSablonuUygula")}</span>{" "}{tx("globalKurallar.dugmesiniKullaninKopyalama")}{" "}<span className="fw-semibold">{tx("globalKurallar.baglantiKurmaz")}</span>{tx("globalKurallar.kopyalandiktanSonraFuarKendiKuralina")}
            </div>
          </div>
        </Alert>
        )}

        {/* Stand tipi kuralları kopyalanmaz — CANLI miras. Uyarı bu yüzden şablonunkinin tersi. */}
        {activeTab === "standTipi" && (
        <Alert color="danger" className="d-flex align-items-start gap-2">
          <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
          <div>
            <div className="fw-semibold mb-1">{tx("globalKurallar.buSekmeAcikFuarlarDahil")}</div>
            <div className="small">
              {tx("globalKurallar.buradakiStandTipiKurallariSablon")}{" "}<span className="fw-semibold">{tx("globalKurallar.canliGlobalVarsayilanlardir")}</span>{tx("globalKurallar.birFuarBirAlaniKendi")}
              <Link to="/Management/Fairs/Rules?tab=standTipi" className="fw-semibold">{tx("globalKurallar.fuarKurallari")}</Link>{" "}
              {tx("globalKurallar.ekranindaOAlaniGecersizKilarak")}
            </div>
          </div>
        </Alert>
        )}

        {GLOBAL_SLOT_TABS.includes(activeTab) && (
        <Card className="mb-3">
          <CardBody className="py-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <Globe size={16} className="text-dark" />
              <span className="fw-semibold">{tx("globalKurallar.globalSablon")}</span>
              {kural?.configured === false
                ? <Badge color="secondary">{tx("globalKurallar.henuzYazilmamis")}</Badge>
                : <Badge color="dark">{tx("globalKurallar.tanimli")}</Badge>}
              <span className="text-muted small">
                {tx("globalKurallar.teknikYuvaFuar", { GLOBAL_KURAL_FUAR_ID })}
              </span>
            </div>
            <Button color="secondary" outline size="sm" onClick={refresh} disabled={loading}>
              <RefreshCw size={14} className="me-1" />{tx("common:yenile")}
            </Button>
          </CardBody>
        </Card>
        )}

        <Nav tabs className="mb-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <NavItem key={t.key}>
                <NavLink active={activeTab === t.key} style={{ cursor: "pointer" }} onClick={() => setActiveTab(t.key)}>
                  <Icon size={14} className="me-1" />{t.label}
                </NavLink>
              </NavItem>
            );
          })}
        </Nav>

        {loading && GLOBAL_SLOT_TABS.includes(activeTab) && (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        )}

        {/* ─────────────── ŞABLON ─────────────── */}
        {!loading && activeTab === "sablon" && (
          <Card>
            <CardHeader className="d-flex align-items-center gap-2">
              <Zap size={16} className="text-primary" />
              <span className="fw-semibold">{tx("globalKurallar.yeniFuarlarinDogacagiKuralSeti")}</span>
            </CardHeader>
            <CardBody>
              <div className="fw-semibold mb-1">{tx("globalKurallar.serbestElektrikHakki")}</div>
              <div className="text-muted small mb-3">
                {tx("globalKurallar.birFuar")}{" "}<span className="fw-semibold">{tx("globalKurallar.buYontemlerdenYalnizcaBirini")}</span>{" "}{tx("globalKurallar.kullanirHakkinAsilmasiBeyaniEngellemez")}
              </div>
              <Row className="g-2 mb-3">
                {/* Sıra: Yok / Sabit / Matematiksel / Kademeli (SERBEST_KW_MODE anahtar sırası). */}
                {Object.values(SERBEST_KW_MODE).map((m) => (
                  <Col md={6} xl={3} key={m}>
                    <Card
                      className={`border h-100 ${kwMode === m ? "border-primary" : ""}`}
                      style={{ cursor: "pointer" }} onClick={() => setKwMode(m)}
                    >
                      <CardBody className="py-2 px-3">
                        <FormGroup check className="mb-0">
                          <Input type="radio" checked={kwMode === m} onChange={() => setKwMode(m)} />
                          <Label check className="small">{SERBEST_KW_MODE_LABELS[m]}</Label>
                        </FormGroup>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              {kwMode === SERBEST_KW_MODE.FLAT && (
                <Row className="g-3 mb-3">
                  <Col md={4}>
                    <FormGroup className="mb-0">
                      <Label size="sm">{tx("globalKurallar.sabitSerbestKW")}</Label>
                      <Input
                        type="number" min="0" step="0.1" bsSize="sm" placeholder={tx("globalKurallar.yok")}
                        value={kuralForm.serbestKw} onChange={(e) => updateKural("serbestKw", e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              )}

              {/* Matematiksel: floor(m² / blok) × kW — yalnızca tam bloklar. */}
              {kwMode === SERBEST_KW_MODE.BLOCK && (
                <div className="mb-3">
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <span>{tx("globalKurallar.her")}</span>
                    <InputGroup size="sm" style={{ width: 130 }}>
                      <Input
                        type="number" min="0" step="0.5" placeholder="5"
                        value={kuralForm.serbestKwBlokM2}
                        onChange={(e) => updateKural("serbestKwBlokM2", e.target.value)}
                      />
                      <InputGroupText>m²</InputGroupText>
                    </InputGroup>
                    <span>{tx("globalKurallar.icin")}</span>
                    <InputGroup size="sm" style={{ width: 130 }}>
                      <Input
                        type="number" min="0" step="0.1" placeholder="2"
                        value={kuralForm.serbestKwBlokKw}
                        onChange={(e) => updateKural("serbestKwBlokKw", e.target.value)}
                      />
                      <InputGroupText>{tx("globalKurallar.kW")}</InputGroupText>
                    </InputGroup>
                  </div>
                  <div className="text-muted small mt-1">
                    {tx("globalKurallar.yalnizcaTamBloklarSayilirOrn")}
                  </div>
                  {blokTouched && blokErrors.length > 0 && (
                    <Alert color="danger" className="mt-2 mb-0 small">
                      <ul className="mb-0">{blokErrors.map((m) => <li key={m}>{m}</li>)}</ul>
                    </Alert>
                  )}
                </div>
              )}

              {kwMode === SERBEST_KW_MODE.BANDS && (
                <div className="mb-3">
                  <div className="text-muted small mb-2">
                    {tx("globalKurallar.araliklar")}{" "}<span className="fw-semibold">{tx("globalKurallar.altUst")}</span>{tx("globalKurallar.altDahilUstHaric0")}
                  </div>
                  <div className="table-responsive">
                    <Table className="align-middle table-nowrap mb-2" size="sm">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 180 }}>{tx("globalKurallar.altSinirM")}</th>
                          <th style={{ width: 180 }}>{tx("globalKurallar.ustSinirM")}</th>
                          <th style={{ width: 180 }}>{tx("globalKurallar.serbestKW")}</th>
                          <th style={{ width: 60 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {bands.map((b, i) => (
                          // eslint-disable-next-line react/no-array-index-key -- kalıcı id yok; PUT tam değiştirme
                          <tr key={i}>
                            <td>
                              <Input type="number" min="0" step="0.5" bsSize="sm" placeholder={tx("globalKurallar.n0Dan")}
                                value={b.minM2 ?? ""} onChange={(e) => updateBand(i, "minM2", e.target.value)} />
                            </td>
                            <td>
                              <Input type="number" min="0" step="0.5" bsSize="sm" placeholder={tx("globalKurallar.sinirsiz")}
                                value={b.maxM2 ?? ""} onChange={(e) => updateBand(i, "maxM2", e.target.value)} />
                            </td>
                            <td>
                              <Input type="number" min="0" step="0.5" bsSize="sm"
                                value={b.serbestKw ?? ""} onChange={(e) => updateBand(i, "serbestKw", e.target.value)} />
                            </td>
                            <td>
                              <Button color="danger" outline size="sm" onClick={() => removeBand(i)}>
                                <Trash2 size={13} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <Button color="secondary" outline size="sm" onClick={addBand}>
                    <Plus size={13} className="me-1" />{tx("globalKurallar.kademeEkle")}
                  </Button>
                  {bandErrors.length > 0 && (
                    <Alert color="danger" className="mt-2 mb-0 small">
                      <ul className="mb-0">{bandErrors.map((m) => <li key={m}>{m}</li>)}</ul>
                    </Alert>
                  )}
                </div>
              )}

              {freeKwPreview != null && (
                <div className="text-muted small mb-4">
                  {tx("globalKurallar.ornek30MLikBir")}{" "}<span className="fw-semibold">{freeKwPreview}{" "}{tx("globalKurallar.kW")}</span>{" "}{tx("globalKurallar.alir")}
                </div>
              )}

              {/* 2026-09-17 (#53): sabit Cam/Boya kartları yerine katalogdan gelen liste. */}
              <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                <div className="fw-semibold flex-grow-1">{tx("globalKurallar.kullanimKurallari")}</div>
                <Button color="link" size="sm" className="p-0 text-muted" onClick={() => setActiveTab("kullanim")}>
                  {tx("globalKurallar.tanimlariYonet")}
                </Button>
              </div>
              <div className="text-muted small mb-3">
                <span className="fw-semibold">{tx("globalKurallar.sorulsun")}</span>{" "}{tx("globalKurallar.ve")}{" "}<span className="fw-semibold">{tx("globalKurallar.izinli")}</span>{" "}{tx("globalKurallar.ayriSorulardirSormadanYasaklayabilirsinizYasakla")}
              </div>
              <div className="mb-4">
                <KullanimKurallariList
                  rules={usageRules} onChange={updateUsageRule} disabled={actionLoading} idPrefix="gk"
                />
              </div>

              {/* FDS #674 / #675: duvar kuralları — global varsayılan; yeni bir fuara "Global şablonu uygula" ile kopyalanır. */}
              <div className="mb-4">
                <DuvarKurallariAlanlari values={kuralForm} onChange={updateKural} disabled={actionLoading} idPrefix="gk" />
              </div>

              {/* 2026-09-04: anahtar kaldırıldı — m² zorunluluğu artık şablona da
                  yazılmayan bir sabit (fuarKuralService → M2_ZORUNLU_SABIT). */}
              <div className="fw-semibold mb-1">{tx("globalKurallar.standAlani")}</div>
              <div className="text-muted small mb-4">
                {tx("globalKurallar.mBeyani")}{" "}<span className="fw-semibold">{tx("globalKurallar.herFuardaZorunludur")}</span>{" "}{tx("globalKurallar.veKapatilamaz")}
              </div>

              {/* 2026-09-17 (#54): "Yeni fuarlarda kimlik doğrulama zorunlu olsun" anahtarı ve
                  uyarısı kaldırıldı — kimlik doğrulama artık kural değil, backend alanı yok sayıyor. */}
              <div className="text-muted small d-flex align-items-start gap-1 mb-4">
                <ShieldCheck size={14} className="flex-shrink-0 mt-1" />
                <span>
                  {tx("globalKurallar.kimlikDogrulamaArtikFuarKurali")}
                </span>
              </div>

              <Button
                color="dark" onClick={handleSaveKural}
                disabled={actionLoading || bandErrors.length > 0 || blokErrors.length > 0}
              >
                {actionLoading ? <Spinner size="sm" className="me-1" /> : <Save size={14} className="me-1" />}
                {tx("globalKurallar.sablonuKaydet")}
              </Button>
            </CardBody>
          </Card>
        )}

        {/* ─────────────── GLOBAL STAND TİPİ KURALLARI (canlı varsayılanlar) ───────────────
            2026-09-17 (#55/#57): eski "Stand Tipi Şablonu" düz tablosu yerine Fuar Kuralları ile
            ortak ağaç, fuarId = 0. Burada her şey GLOBAL çözülür; fuarKural hep null. */}
        {!loading && activeTab === "standTipi" && (
          <StandTipiKuralTree
            fuarId={GLOBAL_KURAL_FUAR_ID}
            forceLive={LIVE}
            tree={typeTree}
            actionLoading={actionLoading}
            runAction={runAction}
            accent="dark"
            footer={
              <>
                {tx("globalKurallar.herAlanOnceTipinKendisinden")}
              </>
            }
          />
        )}

        {/* ─────────────── KULLANIM KURALI TANIMLARI (katalog, #53) ───────────────
            Fuardan bağımsız. Silme yok — pasifleştirilir. Kod oluşturulduktan sonra sabit. */}
        {activeTab === "kullanim" && (
          <Card>
            <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
              <ListChecks size={16} className="text-primary" />
              <span className="fw-semibold flex-grow-1">
                {tx("globalKurallar.kullanimKuraliTanimlari")}{" "}<Badge color="light" className="text-dark ms-1">{katalog.length}</Badge>
              </span>
              <Button color="light" size="sm" className="border" onClick={refreshKatalog} disabled={katalogLoading} title={tx("common:yenile")}>
                <RefreshCw size={14} />
              </Button>
              <Button color="dark" size="sm" onClick={openNewKatalog}>
                <Plus size={14} className="me-1" />{tx("globalKurallar.yeniKural")}
              </Button>
            </CardHeader>
            <CardBody className="p-0">
              <div className="text-muted small px-3 py-2 border-bottom">
                <Info size={13} className="me-1" />
                {tx("globalKurallar.buradaTanimlananHerAktifKural")}{" "}<span className="fw-semibold">{tx("globalKurallar.izinliVeSorulmuyor")}</span>{" "}{tx("globalKurallar.baslarSilinemezPasiflestirilenKuralFuar")}
              </div>
              {katalogLoading ? (
                <div className="text-center py-4"><Spinner color="primary" size="sm" /></div>
              ) : katalog.length === 0 ? (
                <div className="text-muted text-center py-4">{tx("globalKurallar.tanimliKullanimKuraliYok")}</div>
              ) : (
                <div className="table-responsive">
                  <Table className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3" style={{ width: 60 }}>{tx("globalKurallar.sira")}</th>
                        <th style={{ width: 110 }}>{tx("globalKurallar.kod")}</th>
                        <th>{tx("globalKurallar.ad")}</th>
                        <th>{tx("globalKurallar.beyanSorusu")}</th>
                        <th>{tx("globalKurallar.yasakMesaji")}</th>
                        <th style={{ width: 90 }} title={tx("globalKurallar.buKuralIcinDegerKaydedilmis")}>{tx("globalKurallar.fuar")}</th>
                        <th style={{ width: 90 }}>{tx("globalKurallar.durum")}</th>
                        <th style={{ width: 150 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {katalog.map((k) => (
                        <tr key={k.kullanimKuraliId} className={k.aktif ? "" : "opacity-50"}>
                          <td className="ps-3 text-muted">{k.sira ?? "—"}</td>
                          <td><code>{k.kod}</code></td>
                          {/* WAS: <td className="fw-semibold">{k.ad}</td> <td className="small">{k.beyanSorusu}</td>
                              <td className="small text-muted">{k.yasakMesaji || "—"}</td>
                              #337: the stored Turkish, with the English under it (or a "no English" mark). */}
                          <td>
                            <div className="fw-semibold">{k.adTr ?? k.ad}</div>
                            {k.adEn
                              ? <div className="small text-muted">{tx("globalKurallar.enEtiketi", { metin: k.adEn })}</div>
                              : <Badge color="warning-subtle" className="text-warning-emphasis">{tx("globalKurallar.ingilizcesiYok")}</Badge>}
                          </td>
                          <td className="small">
                            <div>{k.beyanSorusuTr ?? k.beyanSorusu}</div>
                            {k.beyanSorusuEn && <div className="text-muted">{tx("globalKurallar.enEtiketi", { metin: k.beyanSorusuEn })}</div>}
                          </td>
                          <td className="small text-muted">
                            <div>{(k.yasakMesajiTr ?? k.yasakMesaji) || "—"}</div>
                            {k.yasakMesajiEn && <div>{tx("globalKurallar.enEtiketi", { metin: k.yasakMesajiEn })}</div>}
                          </td>
                          <td className="text-muted">{k.fuarSayisi ?? "—"}</td>
                          <td>
                            {k.aktif
                              ? <Badge color="success-subtle" className="text-success">{tx("globalKurallar.aktif")}</Badge>
                              : <Badge color="light" className="text-muted border">{tx("globalKurallar.pasif")}</Badge>}
                          </td>
                          <td className="text-end pe-3 text-nowrap">
                            <Button color="link" size="sm" className="text-body p-1" onClick={() => openEditKatalog(k)} title={tx("common:duzenle")} disabled={actionLoading}>
                              <Pencil size={14} />
                            </Button>
                            {k.aktif ? (
                              <Button color="link" size="sm" className="text-danger p-1" disabled={actionLoading} onClick={() => setDeactivateTarget(k)}>
                                {tx("globalKurallar.pasiflestir")}
                              </Button>
                            ) : (
                              <Button color="link" size="sm" className="text-success p-1" disabled={actionLoading} onClick={() => handleSetAktif(k, true)}>
                                {tx("globalKurallar.aktiflestir")}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* ─────────────── FUAR ALANI TAVANLARI ───────────────
            ÜÇÜNCÜ "global" ve bu da kopyalanmaz. Buradaki iki sayı MEKÂNA aittir,
            fuara değil: aynı salonun fiziksel tavanı o mekânda açılan her fuar için
            aynıdır. Daha önce sadece Fuar Kuralları ekranının salon tablosunda
            düzenlenebiliyordu; orada bir fuar seçili olduğu için fuara özelmiş gibi
            okunuyordu. Kapsamı doğru göstermek için buraya taşındı — fuar ekranındaki
            alan da çalışmaya devam ediyor, ikisi aynı satırı yazıyor. */}
        {activeTab === "tavan" && (
          <>
            <Alert color="info" className="d-flex align-items-start gap-2 small">
              <Info size={16} className="flex-shrink-0 mt-1" />
              <span>
                {tx("globalKurallar.buSekme")}{" "}<span className="fw-semibold">{tx("globalKurallar.kopyalamaz")}</span>{tx("globalKurallar.yazdigiIkiSeyDeFuar")}
                <span className="fw-semibold">{tx("globalKurallar.herFuardaAninda")}</span>{" "}{tx("globalKurallar.gecerlidirSuregelenFuarlarDahilFuardan")}
              </span>
            </Alert>

            <Card className="mb-3">
              <CardBody className="py-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <Building2 size={16} className="text-secondary" />
                  <Label className="mb-0 fw-semibold">{tx("globalKurallar.fuarAlani")}</Label>
                  <Input
                    type="select" bsSize="sm" style={{ minWidth: 260 }}
                    value={venueId}
                    onChange={(e) => { setVenueId(e.target.value); setCeilingConflict(null); }}
                  >
                    <option value="">{tx("globalKurallar.secin")}</option>
                    {venues.map((v) => (
                      <option key={v.fuarAlaniId} value={v.fuarAlaniId}>{v.fuarAlaniName}</option>
                    ))}
                  </Input>
                  {tavanLoading && <Spinner size="sm" color="secondary" />}
                </div>
                <Button
                  color="secondary" outline size="sm"
                  onClick={refreshTavan} disabled={!venueId || tavanLoading}
                >
                  <RefreshCw size={14} className="me-1" />{tx("common:yenile")}
                </Button>
              </CardBody>
            </Card>

            {!venueId && (
              <Alert color="secondary">
                {tx("globalKurallar.tavanlariGormekVeYazmakIcin")}
              </Alert>
            )}

            {venueId && !tavanLoading && tavan && (
              <>
                {/* Birleştirme eksik kalmışsa söylemek ZORUNDAYIZ — eksik bir salon
                    listesinde "tavanı yok" ile "listede yok" ayırt edilemez. */}
                {tavan.partial && (
                  <Alert color="warning" className="small py-2">
                    <AlertTriangle size={14} className="me-1" />
                    {tx("globalKurallar.buMekandakiBaziFuarlarinYukseklik")}
                  </Alert>
                )}

                <Card className="mb-3">
                  <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
                    <Ruler size={16} className="text-secondary" />
                    <span className="fw-semibold">{tx("globalKurallar.fuarAlaniSiniri")}</span>
                    <Badge color="secondary">{tx("globalKurallar.fairground")}</Badge>
                    <span className="text-muted small">{tx("globalKurallar.politikaMekaninTamamiIcinTek")}</span>
                  </CardHeader>
                  <CardBody>
                    <div className="text-muted small mb-2">
                      {tx("globalKurallar.buradaAcilan")}{" "}<span className="fw-semibold">{tx("globalKurallar.hicbirFuarVeHicbirSalon")}</span>{" "}{tx("globalKurallar.buSayininUstuneCikamazBos")}
                      <span className="fw-semibold">{tx("globalKurallar.temizler")}</span>.
                    </div>
                    <div className="d-flex gap-1" style={{ maxWidth: 280 }}>
                      <Input
                        type="number" min="0" step="0.1" bsSize="sm"
                        placeholder={tavan.fuarAlaniHMaxKnown ? tx("globalKurallar.kayitYok") : tx("globalKurallar.okunamadi")}
                        value={venueLimitInput}
                        onChange={(e) => setVenueLimitInput(e.target.value)}
                      />
                      <Button
                        color="secondary" outline size="sm"
                        disabled={actionLoading}
                        onClick={() => setVenueConfirm(true)}
                      >
                        <Save size={13} />
                      </Button>
                    </div>
                    {/* Mekânın hiç fuarı yoksa bu sayı OKUNAMAZ (uç bir fuar ister) —
                        "kayıt yok" demek yanlış olurdu. */}
                    {!tavan.fuarAlaniHMaxKnown && (
                      <div className="text-muted small mt-2">
                        {tx("globalKurallar.buMekanaBagliBirFuar")}
                      </div>
                    )}
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
                    <Layers3 size={16} className="text-info" />
                    <span className="fw-semibold">{tx("globalKurallar.salonlarinFizikselTavani")}</span>
                    <Badge color="info">{tx("globalKurallar.ceiling")}</Badge>
                    <Badge color="light" className="text-dark">{tavan.halls.length}</Badge>
                    <span className="text-muted small">{tx("globalKurallar.politikaDegilBinaSonucuHer")}</span>
                  </CardHeader>
                  <CardBody>
                    {tavan.halls.length === 0 ? (
                      <Alert color="secondary" className="mb-0">
                        {tx("globalKurallar.buFuarAlanindaSalonBulunamadi")}
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{tx("globalKurallar.salon")}</th>
                              <th style={{ width: 210 }}>{tx("globalKurallar.fizikselTavanM")}</th>
                              <th>{tx("globalKurallar.buSalondaSozVerilmisEn")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tavan.halls.map((h) => {
                              const dirty =
                                (ceilingInputs[h.salonId] ?? "") !== (h.ceilingHeight == null ? "" : String(h.ceilingHeight));
                              const typed = toNullableNumber(ceilingInputs[h.salonId]);
                              // 409'u kaydetmeden önce söyle: mekândaki fuarlar o salonda
                              // daha yükseğine izin veriyorsa backend yazmayı reddeder.
                              const willConflict =
                                typed != null && h.maxPromised != null && typed < h.maxPromised;
                              return (
                                <tr key={h.salonId}>
                                  <td className="fw-semibold">{h.salonName ?? tx("globalKurallar.salon2", { salonId: h.salonId })}</td>
                                  <td>
                                    <div className="d-flex gap-1">
                                      <Input
                                        type="number" min="0" step="0.1" bsSize="sm"
                                        placeholder={tx("globalKurallar.kayitYok")}
                                        invalid={willConflict}
                                        value={ceilingInputs[h.salonId] ?? ""}
                                        onChange={(e) => setCeilingInputs((prev) => ({ ...prev, [h.salonId]: e.target.value }))}
                                      />
                                      <Button
                                        color="info" outline size="sm"
                                        disabled={actionLoading || !dirty}
                                        onClick={() => handleSaveCeiling(h.salonId)}
                                      >
                                        <Save size={13} />
                                      </Button>
                                    </div>
                                    {willConflict && (
                                      <div className="text-danger small mt-1">
                                        {tx("globalKurallar.buSalondaSozVerilmisReddedilir", { fmtM: fmtM(h.maxPromised) })}
                                      </div>
                                    )}
                                  </td>
                                  <td className="small">
                                    {h.maxPromised == null ? (
                                      <span className="text-muted">{tx("globalKurallar.sozYok")}</span>
                                    ) : (
                                      <>
                                        <span className="fw-semibold me-2">{fmtM(h.maxPromised)}</span>
                                        <span className="text-muted">
                                          {h.promises
                                            .filter((pr) => Number(pr.salonHMax) === h.maxPromised)
                                            .map((pr) => pr.fuarName)
                                            .join(", ")}
                                        </span>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                    <div className="text-muted small mt-3">
                      <Info size={13} className="me-1" />
                      {tx("globalKurallar.tavaniTemizlemekHerZamanSerbesttir")}
                      <span className="fw-semibold">{tx("globalKurallar.indirmek")}</span>{tx("globalKurallar.oSalondaDahaFazlasiniSoz")}
                    </div>
                  </CardBody>
                </Card>
              </>
            )}
          </>
        )}

        {/* #430: company document rules by nationality, every fair */}
        {activeTab === "firmaBelge" && <FirmaBelgeKurallariKarti fuarId={0} />}

        {/* #374: PPE (KKD). The template is COPIED into a fair (Fuar Kuralları › KKD › Apply global template), not inherited; the catalogue
            is shared by every fair, so a new / renamed / retired item re-reads the template above it. */}
        {activeTab === "kkd" && (
          <>
            <KkdKurallariKarti fuarId={0} reloadToken={kkdKatalogToken} />
            <KkdKalemKatalogu onChanged={() => setKkdKatalogToken((n) => n + 1)} />
          </>
        )}

        {/* ─────────────── GLOBAL BELGE KURALLARI ─────────────── */}
        {activeTab === "belge" && (
          <>
            {/* İkinci "global" — ve bu gerçekten global. Ayrımı burada tekrar
                yazıyoruz çünkü kullanıcı aynı sayfada iki farklı semantiği yan yana
                görüyor ve ikisini de "global" diye okuyor. */}
            <Alert color="info" className="d-flex align-items-start gap-2 small">
              <Info size={16} className="flex-shrink-0 mt-1" />
              <span>
                {tx("globalKurallar.kuralSablonununAksineBuKurallar")}{" "}<span className="fw-semibold">{tx("globalKurallar.kopyalanmaz")}</span>{" "}{tx("globalKurallar.gercektenGlobaldirlerZorunluBelgeKaskadinda")}{" "}<span className="fw-semibold">{tx("globalKurallar.mevcutFuarlariDa")}</span>{" "}{tx("globalKurallar.etkilerFuaraOzelBirKural")}
              </span>
            </Alert>
            <BelgeKurallariPage scope="global" embedded />
          </>
        )}

        {/* ─── Fuar alanı sınırı onayı ───
            Bu ekranın diğer yazmaları ŞABLONA gider ve geçmişi etkilemez. Bu yazma
            ise mekândaki her fuarda anında yürürlüğe girer — o yüzden ayrı onay. */}
        <Modal isOpen={venueConfirm} toggle={() => !actionLoading && setVenueConfirm(false)} centered>
          <ModalHeader toggle={() => !actionLoading && setVenueConfirm(false)}>
            {tx("globalKurallar.fuarAlaniSiniri")}
          </ModalHeader>
          <ModalBody>
            <Alert color="warning" className="small mb-3">
              <AlertTriangle size={15} className="me-1" />
              {tx("globalKurallar.buBirSablonDegil")}{" "}<span className="fw-semibold">{tavan?.fuarAlaniName ?? tx("globalKurallar.buFuarAlaninda")}</span>{" "}
              {tx("globalKurallar.acilan")}{" "}<span className="fw-semibold">{tx("globalKurallar.herFuari")}</span>{" "}{tx("globalKurallar.anindaEtkiler")}
            </Alert>
            <p className="mb-2">
              {toNullableNumber(venueLimitInput) == null ? (
                <>{tx("globalKurallar.fuarAlanininSiniri")}{" "}<span className="fw-semibold">{tx("globalKurallar.temizlenecek")}</span>{tx("globalKurallar.buSeviyedenGelenTavanOrtadan")}</>
              ) : (
                <>{tx("globalKurallar.fuarAlanininSiniri_2")}
                  <span className="fw-semibold">{fmtM(toNullableNumber(venueLimitInput))}</span>{" "}{tx("globalKurallar.olacakBundanSonraHicbirFuar")}</>
              )}
            </p>
            <p className="text-muted small mb-0">
              {tx("globalKurallar.buMekandakiFuarlardanBiriYa")}
              <span className="fw-semibold">{" "}{tx("globalKurallar.reddedilir")}</span>{" "}{tx("globalKurallar.veEngelleyenlerIsimleriyleListelenir")}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline disabled={actionLoading} onClick={() => setVenueConfirm(false)}>
              {tx("globalKurallar.vazgec")}
            </Button>
            <Button
              color="warning" disabled={actionLoading}
              onClick={async () => { const ok = await handleSaveVenueLimit(); if (ok) setVenueConfirm(false); }}
            >
              {actionLoading ? <Spinner size="sm" /> : tx("common:kaydet")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Stand tipi kuralı ekle/düzenle/kaldır modalları StandTipiKuralTree'nin içinde
            (2026-09-17). */}

        {/* ─── Kullanım kuralı ekle/düzenle ─── */}
        <Modal isOpen={katalogModal} toggle={() => !actionLoading && setKatalogModal(false)} centered>
          <ModalHeader toggle={() => !actionLoading && setKatalogModal(false)}>
            {katalogEditing ? tx("globalKurallar.kullanimKurali", { ad: katalogEditing.ad }) : tx("globalKurallar.yeniKullanimKurali")}
          </ModalHeader>
          <ModalBody>
            {katalogEditing && (
              <FormGroup>
                <Label size="sm">{tx("globalKurallar.kod")}</Label>
                {/* Kod değişmez — beyanlar ve ihlaller ona bağlı. */}
                <div><code>{katalogEditing.kod}</code> <span className="text-muted small ms-1">{tx("globalKurallar.degistirilemez")}</span></div>
              </FormGroup>
            )}
            <FormGroup>
              <Label size="sm">{tx("globalKurallar.ad")}{" "}<span className="text-danger">*</span></Label>
              <Input
                bsSize="sm" placeholder={tx("globalKurallar.cam")} value={katalogForm.ad}
                onChange={(e) => setKatalogForm((f) => ({ ...f, ad: e.target.value }))}
              />
              {!katalogEditing && (
                <div className="text-muted small mt-1">{tx("globalKurallar.kodAddanOtomatikUretilirVe")}</div>
              )}
            </FormGroup>
            <FormGroup>
              <Label size="sm">{tx("globalKurallar.beyanSorusu")}{" "}<span className="text-danger">*</span></Label>
              <Input
                bsSize="sm" placeholder={tx("globalKurallar.standimdaCamKullanilacak")} value={katalogForm.beyanSorusu}
                onChange={(e) => setKatalogForm((f) => ({ ...f, beyanSorusu: e.target.value }))}
              />
              <div className="text-muted small mt-1">{tx("globalKurallar.dekoratorunBeyanFormundaIsaretleyecegiCumle")}</div>
            </FormGroup>
            <FormGroup>
              <Label size="sm">{tx("globalKurallar.yasakMesaji")}</Label>
              <Input
                type="textarea" rows={2} bsSize="sm"
                placeholder={tx("globalKurallar.buFuardaCamKullaniminaIzin")}
                value={katalogForm.yasakMesaji}
                onChange={(e) => setKatalogForm((f) => ({ ...f, yasakMesaji: e.target.value }))}
              />
            </FormGroup>
            {/* #337: the English texts. Left blank, the rule reads in Turkish on English screens. */}
            <div className="border-top pt-3 mb-3">
              <div className="fw-semibold small mb-1">{tx("globalKurallar.ingilizce")}</div>
              <div className="text-muted small mb-2">{tx("globalKurallar.ingilizceIpucu")}</div>
              <FormGroup>
                <Label size="sm">{tx("globalKurallar.adEn")}</Label>
                <Input
                  bsSize="sm" placeholder={tx("globalKurallar.adEnOrnek")} value={katalogForm.adEn}
                  onChange={(e) => setKatalogForm((f) => ({ ...f, adEn: e.target.value }))}
                />
              </FormGroup>
              <FormGroup>
                <Label size="sm">{tx("globalKurallar.beyanSorusuEn")}</Label>
                <Input
                  bsSize="sm" placeholder={tx("globalKurallar.beyanSorusuEnOrnek")} value={katalogForm.beyanSorusuEn}
                  onChange={(e) => setKatalogForm((f) => ({ ...f, beyanSorusuEn: e.target.value }))}
                />
              </FormGroup>
              <FormGroup className="mb-0">
                <Label size="sm">{tx("globalKurallar.yasakMesajiEn")}</Label>
                <Input
                  type="textarea" rows={2} bsSize="sm"
                  placeholder={tx("globalKurallar.yasakMesajiEnOrnek")}
                  value={katalogForm.yasakMesajiEn}
                  onChange={(e) => setKatalogForm((f) => ({ ...f, yasakMesajiEn: e.target.value }))}
                />
              </FormGroup>
              {katalogEnEksik && <div className="text-danger small mt-1">{tx("globalKurallar.ingilizceAdGerekli")}</div>}
            </div>
            <FormGroup className="mb-0" style={{ maxWidth: 140 }}>
              <Label size="sm">{tx("globalKurallar.sira")}</Label>
              <Input
                type="number" min="0" bsSize="sm" value={katalogForm.sira}
                onChange={(e) => setKatalogForm((f) => ({ ...f, sira: e.target.value }))}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setKatalogModal(false)} disabled={actionLoading}>{tx("globalKurallar.vazgec")}</Button>
            <Button color="dark" onClick={handleSaveKatalog} disabled={actionLoading || katalogInvalid}>
              {actionLoading ? <Spinner size="sm" className="me-1" /> : <Save size={14} className="me-1" />}{tx("common:kaydet")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* ─── Pasifleştirme onayı — her fuarın ekranını ve beyan formunu etkiler ─── */}
        <Modal isOpen={Boolean(deactivateTarget)} toggle={() => !actionLoading && setDeactivateTarget(null)} centered>
          <ModalHeader toggle={() => !actionLoading && setDeactivateTarget(null)}>{tx("globalKurallar.kullanimKuraliniPasiflestir")}</ModalHeader>
          <ModalBody>
            <p className="mb-2">
              <span className="fw-semibold">{deactivateTarget?.ad}</span> (<code>{deactivateTarget?.kod}</code>{tx("globalKurallar.pasiflestirilecek")}
            </p>
            <p className="text-muted small mb-0">
              {tx("globalKurallar.pasifKuralHicbirFuarinKural")}
              {deactivateTarget?.fuarSayisi ? tx("globalKurallar.suAnFuardaDegeriKayitli", { fuarSayisi: deactivateTarget.fuarSayisi }) : ""}{tx("globalKurallar.silinmezIstediginizZamanYenidenAktiflestirebilir")}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setDeactivateTarget(null)} disabled={actionLoading}>{tx("globalKurallar.vazgec")}</Button>
            <Button
              color="danger" disabled={actionLoading}
              onClick={async () => {
                const ok = await handleSetAktif(deactivateTarget, false);
                if (ok) setDeactivateTarget(null);
              }}
            >
              {actionLoading ? <Spinner size="sm" className="me-1" /> : null}{tx("globalKurallar.pasiflestir")}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}
